import React, { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Switch } from "../ui/switch";
import { api, errorMessage } from "../../lib/api";

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

/** One plan x one region: With Teams / No Teams + estimated flag. Saves the merged `regional` map. */
export default function RegionalPlanRow({ plan, region, adminKey, onSaved }) {
  const local = plan.regional?.[region.id];
  const [teams, setTeams] = useState(local?.teams ?? "");
  const [noTeams, setNoTeams] = useState(local?.noTeams ?? "");
  const [estimated, setEstimated] = useState(!!local?.estimated);
  const [busy, setBusy] = useState(false);
  const dirty = String(teams) !== String(local?.teams ?? "") || String(noTeams) !== String(local?.noTeams ?? "") || estimated !== !!local?.estimated;
  const suggested = plan.price ? { teams: +(plan.price.teams * (region.factor || 1)).toFixed(2), noTeams: +(plan.price.noTeams * (region.factor || 1)).toFixed(2) } : null;

  const save = async () => {
    const t = num(teams);
    const n = num(noTeams);
    if (t === null || n === null || Number.isNaN(t) || Number.isNaN(n)) return toast.error("Enter both prices", { description: "Use the USD x factor suggestion if there is no official list price." });
    setBusy(true);
    try {
      const updated = await api.admin.patchPlan(adminKey, plan.id, { regional: { ...(plan.regional || {}), [region.id]: { ...(local || {}), teams: t, noTeams: n, estimated } } });
      onSaved(updated);
      toast.success(`${plan.short} · ${region.label} saved`);
    } catch (err) {
      toast.error("Update failed", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  return (
    <tr className="feature-row" data-testid={`regional-plan-${plan.id}`}>
      <td>
        <div className="plan-name">{plan.short}</div>
        <div className="plan-full">{plan.name}</div>
      </td>
      <td className="mono muted small">{plan.price ? `$${plan.price.teams} / $${plan.price.noTeams}` : plan.priceLabel || "—"}</td>
      <td>
        <input className="price-input" value={teams} onChange={(e) => setTeams(e.target.value)} placeholder={suggested ? String(suggested.teams) : ""} aria-label={`${region.label} price with Teams`} data-testid={`regional-teams-${plan.id}`} />
      </td>
      <td>
        <input className="price-input" value={noTeams} onChange={(e) => setNoTeams(e.target.value)} placeholder={suggested ? String(suggested.noTeams) : ""} aria-label={`${region.label} price without Teams`} data-testid={`regional-noteams-${plan.id}`} />
      </td>
      <td>
        <label className="filter-item">
          <Switch checked={estimated} disabled={busy} onCheckedChange={setEstimated} aria-label="Estimated price" data-testid={`regional-estimated-${plan.id}`} />
          <span className="muted small">{estimated ? "≈ estimated" : "official"}</span>
        </label>
      </td>
      <td>
        <div className="admin-actions" style={{ margin: 0, gap: 6 }}>
          {suggested && !local && (
            <button type="button" className="tb-btn sm ghost" onClick={() => (setTeams(suggested.teams), setNoTeams(suggested.noTeams), setEstimated(true))} data-testid={`regional-suggest-${plan.id}`} title={`USD x ${region.factor}`}>
              Use USD x {region.factor}
            </button>
          )}
          <button type="button" className="tb-btn sm" disabled={!dirty || busy} onClick={save} data-testid={`regional-save-${plan.id}`}>
            <Check size={13} /> Save
          </button>
        </div>
      </td>
    </tr>
  );
}
